import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import api from "@/lib/api";
import { ListingPhoto } from "@/components/PhotoPlaceholder";
import ListingBadges from "@/components/ListingBadges";
import BestOfferBadge from "@/components/BestOfferBadge";
import { AGE_GROUPS, formatGBP, conditionLabel, CLOTHING_SUBCATEGORIES, CLOTHING_GENDERS, CLOTHING_SIZE_BANDS, CLOTHING_FOOTWEAR_SIZES, CLOTHING_BRANDS } from "@/lib/marketplace";
import { Search, Tag, ShieldCheck, Star, Zap, Lock, ChevronRight } from "lucide-react";
import { subscribeListingUpdates, mergeListingInto } from "@/lib/listingBus";
import { useKidAuth } from "@/context/KidAuthContext";

// Iteration 171 — Listing-type filter chips on /browse. Values match the
// backend `category` filter (see backend/mappings.py CATEGORY_MAP). We keep
// "exchange" as a legacy-alias chip since some historical listings still
// carry that primary category even though the picker now consolidates
// exchange under "free". Empty value = show every listing type.
const LISTING_TYPES = [
  { value: "",         label: "All types" },
  { value: "exchange", label: "Exchange" },
  { value: "free",     label: "Free giveaways" },
  { value: "clothing", label: "Clothing" },
  { value: "other",    label: "Other" },
];

export default function Browse() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    q: params.get("q") || "",
    age_group: params.get("age_group") || "",
    category: params.get("category") || "",
    max_price: params.get("max_price") || "",
    // Iteration 274 — clothing attribute filters, edited by the dynamic
    // clothing row that appears when the Clothing type chip is active.
    clothing_subcategory: params.get("clothing_subcategory") || "",
    clothing_gender: params.get("clothing_gender") || "",
    clothing_size: params.get("clothing_size") || "",
    clothing_brand: params.get("clothing_brand") || "",
  });

  // Iteration 261 — when a KID session is browsing, fetch their Instant
  // Buy rule once so eligible cards get the "⚡ Instant Buy" pill.
  const { kid: kidSession } = useKidAuth();
  const [instantRule, setInstantRule] = useState(null);
  useEffect(() => {
    if (!kidSession?.kid_id) return;
    api.get("/kid/auto-approve")
      .then(({ data }) => setInstantRule(data))
      .catch(() => setInstantRule(null));
  }, [kidSession?.kid_id]);

  // Iteration 304 — after the first paint, idle-prefetch the remaining
  // grid thumbnails into the browser cache so scrolling / paging feels
  // instant (above-the-fold cards are already loading="eager").
  useEffect(() => {
    if (!items.length) return undefined;
    const urls = items.slice(8).map((i) => i.photos?.[0]).filter(Boolean);
    if (!urls.length) return undefined;
    const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 700));
    const cancel = window.cancelIdleCallback || clearTimeout;
    const id = idle(() => {
      urls.forEach((u) => { const im = new Image(); im.src = u; });
    });
    return () => cancel(id);
  }, [items]);

  // Iteration 305 — aggressive scroll prefetch: when the sentinel pinned
  // at ~70% of the grid enters the viewport, warm the NEXT 12 thumbnails
  // (into the service-worker CacheFirst store) plus the next 4 listing
  // detail JSON payloads so product pages open instantly.
  const [prefetchedUpTo, setPrefetchedUpTo] = useState(0);
  useEffect(() => {
    const sentinel = document.getElementById("browse-prefetch-sentinel");
    if (!sentinel || items.length < 8) return undefined;
    const obs = new IntersectionObserver((entries) => {
      if (!entries.some((e) => e.isIntersecting)) return;
      setPrefetchedUpTo((done) => {
        const next = Math.min(done + 12, items.length);
        items.slice(done, next).forEach((it, i) => {
          const u = it.photos?.[0];
          if (u) { const im = new Image(); im.src = u; }
          if (u) fetch(u).catch(() => { /* warm SW cache */ });
          if (i < 4 && it.id) api.get(`/listings/${it.id}`).catch(() => { /* prefetch best-effort */ });
        });
        return next;
      });
    }, { rootMargin: "200px" });
    obs.observe(sentinel);
    return () => obs.disconnect();
  }, [items]);

  // Iteration 171 — accept an explicit `override` so chip clicks can pass
  // the freshly-computed filter set without waiting on setState. Falls
  // back to the closure state for legacy call sites (Enter key, Apply).
  const load = async (override) => {
    const active = override || filters;
    setLoading(true);
    try {
      const query = {};
      Object.entries(active).forEach(([k, v]) => { if (v) query[k] = v; });
      const { data } = await api.get("/listings", { params: query });
      setItems(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Global revalidation — when any listing is edited (this tab, another
  // tab, or the modal), splice the fresh payload into the current grid so
  // badges update instantly without a full refetch.
  useEffect(() => {
    return subscribeListingUpdates((updated) => {
      setItems((prev) => mergeListingInto(prev, updated));
    });
  }, []);

  const applyFilters = (override) => {
    const active = override || filters;
    const p = new URLSearchParams();
    Object.entries(active).forEach(([k, v]) => { if (v) p.set(k, v); });
    setParams(p); load(active);
  };

  return (
    <div className="min-h-screen bg-[#FFFDF7]">
      <Navbar />
      <section className="max-w-7xl mx-auto px-6 lg:px-8 pt-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="pill bg-emerald-100 text-emerald-800">Marketplace</div>
            <h1 className="font-display text-4xl md:text-5xl font-semibold text-slate-900 mt-3">Used - Good Condition toys, fairly priced</h1>
            <p className="text-slate-600 mt-2">Every listing parent-verified and AI-safety-checked.</p>
          </div>
          <div className="text-sm text-slate-500" data-testid="browse-count">{items.length} listings</div>
        </div>

        <div className="mt-8 card-base p-4 md:p-5 flex flex-wrap items-center gap-3" data-testid="browse-filter-bar">
          <div className="flex items-center gap-2 flex-1 min-w-[220px]">
            <Search className="w-4 h-4 text-slate-400" />
            <input className="input-base !bg-transparent !border-0 !ring-0 !p-0" placeholder="Search toys…"
              value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              onKeyDown={(e) => e.key === "Enter" && applyFilters()} data-testid="browse-search-input" />
          </div>
          <div className="flex flex-wrap gap-2" data-testid="browse-type-pills">
            {LISTING_TYPES.map((t) => (
              <button
                key={t.value || "all"}
                onClick={() => {
                  const next = { ...filters, category: t.value };
                  // Iteration 274 — leaving the Clothing chip clears the
                  // clothing attribute filters so stale picks never leak
                  // into another category's query.
                  if (t.value !== "clothing") {
                    next.clothing_subcategory = "";
                    next.clothing_gender = "";
                    next.clothing_size = "";
                    next.clothing_brand = "";
                  }
                  setFilters(next); applyFilters(next);
                }}
                className={`pill cursor-pointer ${filters.category === t.value ? "bg-emerald-500 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
                data-testid={`browse-type-${t.value || "all"}`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2" data-testid="browse-age-pills">
            {AGE_GROUPS.map((a) => (
              <button key={a.value || "all"} onClick={() => { const next = { ...filters, age_group: a.value }; setFilters(next); applyFilters(next); }}
                className={`pill cursor-pointer ${filters.age_group === a.value ? "bg-blue-500 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
                data-testid={`browse-age-${a.value || "all"}`}>{a.label}</button>
            ))}
          </div>
          <input className="input-base !w-36 !py-2" placeholder="Max £" type="number" value={filters.max_price}
            onChange={(e) => setFilters({ ...filters, max_price: e.target.value })}
            onBlur={applyFilters} data-testid="browse-max-price" />
          <button onClick={applyFilters} className="btn-primary !py-2 !px-5 text-sm" data-testid="browse-apply-btn">Apply</button>
        </div>

        {/* Iteration 291 — locked teaser for the PIN-protected CSR Family
            Support Hub. The hub lives outside the public FREE category. */}
        <Link
          to="/csr"
          className="mt-4 card-base p-4 flex items-center gap-3 hover:border-brandTeal hover:shadow-sm transition group"
          data-testid="browse-csr-teaser"
        >
          <div className="w-10 h-10 rounded-2xl bg-brandTeal/10 text-brandTeal inline-flex items-center justify-center flex-shrink-0">
            <Lock className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <div className="font-display font-semibold text-brandSlate">CSR Family Support Hub</div>
            <div className="text-xs text-slate-500">PIN-protected access for community support partners</div>
          </div>
          <ChevronRight className="w-4 h-4 ml-auto text-slate-300 group-hover:text-brandTeal transition" />
        </Link>

        {/* Iteration 274 — dynamic Clothing filter row: appears only when
            the Clothing type chip is active. Subcategory + gender are
            pills; size + brand are grouped selects (too many options for
            pills). Every change applies instantly like the other chips. */}
        {filters.category === "clothing" && (
          <div className="mt-3 card-base p-4 md:p-5" data-testid="browse-clothing-filters">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mr-1">Clothing type</span>
              <button
                onClick={() => { const next = { ...filters, clothing_subcategory: "" }; setFilters(next); applyFilters(next); }}
                className={`pill cursor-pointer ${!filters.clothing_subcategory ? "bg-brandPink text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
                data-testid="browse-clothing-sub-all"
              >All</button>
              {CLOTHING_SUBCATEGORIES.map((s) => (
                <button
                  key={s.value}
                  onClick={() => {
                    const next = {
                      ...filters,
                      clothing_subcategory: filters.clothing_subcategory === s.value ? "" : s.value,
                      // Footwear sizes only apply to shoes — clear a
                      // stale clothing-size pick when switching lanes.
                      clothing_size: "",
                    };
                    setFilters(next); applyFilters(next);
                  }}
                  className={`pill cursor-pointer ${filters.clothing_subcategory === s.value ? "bg-brandPink text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
                  data-testid={`browse-clothing-sub-${s.value}`}
                >{s.label}</button>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mr-1">Gender</span>
              <button
                onClick={() => { const next = { ...filters, clothing_gender: "" }; setFilters(next); applyFilters(next); }}
                className={`pill cursor-pointer ${!filters.clothing_gender ? "bg-blue-500 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
                data-testid="browse-clothing-gender-all"
              >All</button>
              {CLOTHING_GENDERS.map((g) => (
                <button
                  key={g.value}
                  onClick={() => { const next = { ...filters, clothing_gender: filters.clothing_gender === g.value ? "" : g.value }; setFilters(next); applyFilters(next); }}
                  className={`pill cursor-pointer ${filters.clothing_gender === g.value ? "bg-blue-500 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
                  data-testid={`browse-clothing-gender-${g.value}`}
                >{g.label}</button>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-3 mt-3">
              <label className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                Size
                <select
                  className="input-base !w-44 !py-2 !text-xs !font-bold !normal-case !tracking-normal"
                  value={filters.clothing_size}
                  onChange={(e) => { const next = { ...filters, clothing_size: e.target.value }; setFilters(next); applyFilters(next); }}
                  data-testid="browse-clothing-size"
                >
                  <option value="">All sizes</option>
                  {filters.clothing_subcategory !== "shoes_footwear" && (
                    <optgroup label="Clothing sizes">
                      {CLOTHING_SIZE_BANDS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </optgroup>
                  )}
                  {(filters.clothing_subcategory === "shoes_footwear" || !filters.clothing_subcategory) && (
                    <optgroup label="Footwear (UK)">
                      {CLOTHING_FOOTWEAR_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </optgroup>
                  )}
                </select>
              </label>
              <label className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                Brand
                <select
                  className="input-base !w-36 !py-2 !text-xs !font-bold !normal-case !tracking-normal"
                  value={filters.clothing_brand}
                  onChange={(e) => { const next = { ...filters, clothing_brand: e.target.value }; setFilters(next); applyFilters(next); }}
                  data-testid="browse-clothing-brand"
                >
                  <option value="">All brands</option>
                  {CLOTHING_BRANDS.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </label>
            </div>
          </div>
        )}

        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 pb-20" data-testid="browse-grid">
          {loading ? Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="card-base p-4 animate-pulse">
              <div className="h-48 bg-slate-100 rounded-2xl mb-3" />
              <div className="h-4 w-3/4 bg-slate-100 rounded" />
              <div className="h-3 w-1/2 bg-slate-100 rounded mt-2" />
            </div>
          )) : items.length === 0 ? (
            <div className="col-span-full card-base p-10 text-center text-slate-500" data-testid="browse-empty">
              No listings match your filters yet.
            </div>
          ) : items.map((it, cardIdx) => {
            const isReserved = ["reserved", "pending_parent_approval"].includes(it.status);
            const isSold = it.status === "sold";
            // Iteration 261 — kid-session Instant Buy pill: total cost
            // (price + 10% fee) within the child's auto-approve limit.
            const instantEligible = !!(instantRule?.enabled) && !isReserved && !isSold
              && Math.round((it.price || 0) * 110) <= (instantRule?.threshold_pennies || 0);
            return (
            <React.Fragment key={it.id}>
            {cardIdx === Math.floor(items.length * 0.7) && (
              /* Iteration 305 — ~70% scroll sentinel: entering the viewport
                 triggers the next-12 thumbnail + detail JSON prefetch. */
              <div id="browse-prefetch-sentinel" data-testid="browse-prefetch-sentinel" className="h-px w-px overflow-hidden" aria-hidden="true" />
            )}
            <Link to={`/listing/${it.id}`} className={`card-base overflow-hidden block hover:-translate-y-1 hover:shadow-pop transition-all ${isSold ? "opacity-75" : ""}`} data-testid={`listing-card-${it.id}`}>
              <div className="aspect-square bg-slate-100 relative">
                <ListingPhoto src={it.photos?.[0]} alt={it.title} eager={cardIdx < 8} blur={it.photo_blur || ""} className={`w-full h-full object-cover ${isSold ? "grayscale" : ""}`} />
                {instantEligible && (
                  <span className="absolute bottom-2 left-2 pill bg-emerald-500 text-white shadow-soft inline-flex items-center" data-testid={`listing-instant-buy-${it.id}`}><Zap className="w-3 h-3 mr-1" /> Instant Buy</span>
                )}
                {it.is_premium && (
                  <span className="absolute top-2 left-2 pill bg-amber-400 text-amber-950 shadow-soft" data-testid={`listing-featured-${it.id}`}><Star className="w-3 h-3 mr-1 fill-amber-900" /> Featured</span>
                )}
                {!it.is_premium && it.seller_spotlight_active && (
                  <span className="absolute top-2 left-2 pill bg-blue-100 text-blue-800 shadow-soft" data-testid={`listing-spotlight-${it.id}`}>✨ Spotlight seller</span>
                )}
                {it.pay_what_you_can && !isReserved && !isSold && (
                  <div className="absolute top-2 right-2">
                    <BestOfferBadge size="sm" testId={`listing-best-offer-${it.id}`} />
                  </div>
                )}
                {isReserved && (
                  <div className="absolute inset-x-0 bottom-0 bg-amber-500/90 text-white px-3 py-2 text-center text-xs font-bold uppercase tracking-wider" data-testid={`listing-reserved-badge-${it.id}`}>
                    Reserved · Pending Parent Approval
                  </div>
                )}
                {isSold && (
                  <div className="absolute inset-x-0 bottom-0 bg-slate-800/90 text-white px-3 py-2 text-center text-xs font-bold uppercase tracking-wider" data-testid={`listing-sold-badge-${it.id}`}>
                    Sold
                  </div>
                )}
              </div>
              <div className="p-5">
                {/* Iteration 76 — canonical badge stack */}
                <ListingBadges
                  listing={it}
                  only={["status", "new", "category", "condition", "clothing", "parcel", "parent_posted", "cause", "fundraise"]}
                  className="mb-2"
                />
                <div className="font-display text-lg font-medium text-slate-900 line-clamp-1">{it.title}</div>
                <div className="flex items-center justify-between mt-2">
                  <div className="text-2xl font-display font-semibold text-blue-600">{formatGBP(it.price)}</div>
                  {it.seller_id_verified && (
                    <div
                      className="text-xs text-slate-500 inline-flex items-center gap-1"
                      title="Seller's ID and adult status have been verified"
                      data-testid={`listing-verified-parent-${it.id}`}
                    >
                      <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" /> Adult Verified Parent
                    </div>
                  )}
                </div>
                {it.kid_alias && (
                  <div className="mt-2 text-xs text-slate-500 inline-flex items-center gap-1">
                    <Tag className="w-3 h-3" /> Sold by {it.kid_alias}
                    {it.seller_avg_rating != null && (<span className="ml-1 inline-flex items-center text-amber-600"><Star className="w-3 h-3 fill-amber-500 mr-0.5" />{it.seller_avg_rating.toFixed(1)}</span>)}
                  </div>
                )}
              </div>
            </Link>
            </React.Fragment>
            );
          })}
        </div>
      </section>
      <Footer />
    </div>
  );
}
